import { Injectable } from '@angular/core';
import { PoNotificationService } from '@po-ui/ng-components';
import { PoStorageService } from '@po-ui/ng-storage';
import { PoNetworkType, PoSyncConfig, PoSyncService } from '@po-ui/ng-sync';
import { BehaviorSubject, Observable } from 'rxjs';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import { BranchService } from './branch/branch.service';
import { CompanyService } from './company/company.service';
import { LiteralsService } from './literals-service';
import { NetworkService } from './network.service';
import { SchemasService } from './schemas.service';
@Injectable({
  providedIn: 'root'
})
export class SetupSyncService {
  errorLoadData$ = new BehaviorSubject<boolean>(false);
  stillSyncing$ = new BehaviorSubject<boolean>(false);
  isFullDataLoadSync$ = new BehaviorSubject<boolean>(false);
  literals: LiteralsWords;

  config: PoSyncConfig = {
    type: [
      PoNetworkType.ethernet,
      PoNetworkType.wifi,
      // eslint-disable-next-line no-underscore-dangle
      PoNetworkType._2g,
      // eslint-disable-next-line no-underscore-dangle
      PoNetworkType._3g,
      // eslint-disable-next-line no-underscore-dangle
      PoNetworkType._4g
    ],
    period: 600
  };

  hasUpdate = new BehaviorSubject<boolean>(false);

  constructor(
    private schemasService: SchemasService,
    private poNotificationService: PoNotificationService,
    private poSync: PoSyncService,
    private networkService: NetworkService,
    private poStorageService: PoStorageService,
    private literalsService: LiteralsService,
    private companyService: CompanyService,
    private branchService: BranchService
  ) {
    this.literals = this.literalsService.getLiteralModule();
  }

  async prepareSync(): Promise<boolean> {
    return await new Promise((resolve, reject) => {
      this.schemasService
        .getSchemaAll()
        .then(async schemas => {
          if (schemas.length > 0) {
            await this.poSync
              .prepare(schemas, this.config)
              .then(() => {
                this.poSync.onSync().subscribe(() => this.completeSync());
                resolve(true);
              })
              .catch(error => reject(error));
          } else {
            reject(this.literals.str0508);
          }
        })
        .catch(error => reject(error));
    });
  }

  async startSync(): Promise<boolean> {
    return await this.poSync.sync();
  }

  completeSync(): void {
    this.hasUpdate.next(true);
  }

  async loadDataControllSync(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.isFullDataLoadSync = false;
      this.poSync.loadData().subscribe({
        next: () => {
          localStorage.setItem('errorLoadData', 'false');
          this.startSync();
          resolve(true);
        },
        error: error => {
          localStorage.setItem('errorLoadData', 'true');
          this.destroySync().then(() => {
            this.poStorageService.clear();
          });
          reject(error);
        },
        complete: () => this.isFullDataLoadSync = true
      });
    });
  }

  set isFullDataLoadSync(value: boolean) {
    localStorage.setItem('isFullDataLoadSync', `${value}`);
    this.isFullDataLoadSync$.next(this.isFullDataLoadSync);
  }

  get isFullDataLoadSync(): boolean {
    return localStorage.getItem('isFullDataLoadSync') === 'true';
  }

  checkErrorLoadData(): boolean {
    if (localStorage.getItem('errorLoadData') === 'true') {
      return true;
    }
    return false;
  }

  async reloadAsync(): Promise<void> {
    this.destroySync()
      .then(async () => {
        await this.prepareSync();
        this.loadDataControllSync().then(() => this.startSync());
      })
      .catch(error => this.poNotificationService.error(error));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadDataPoSync(): Observable<Array<{ entity: string; data: any[] }>> {
    return this.poSync.loadData();
  }

  enableSync(): void {
    this.poSync.enableSync();
  }

  disableSync(): void {
    this.poSync.disableSync();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destroySync(): Promise<any> {
    return this.poSync.destroy();
  }

  getSync(): Observable<boolean> {
    return this.hasUpdate.asObservable();
  }

  getStatus(): boolean {
    return this.networkService.getConnectionStatus().status;
  }

  changeCompanyAndBranch(company: string, branch: string): void {
    this.companyService.setCompanyToStorage(company);
    this.branchService.setBranchToStorage(branch);
  }

  cleanCompanyAndBranch(): void {
    this.companyService.cleanStorageCompany();
    this.branchService.cleanStorageBranch();
  }
}
